import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, CheckCircle, Edit3, Plus, RefreshCw, Save, XCircle } from 'lucide-react';
import server from '../../../api/server';
import { useToast } from '../../../context/ToastContext';

const PROMPT_KEY_RE = /^[a-zA-Z0-9-]+$/;

const EMPTY_FORM = {
  prompt_key: '',
  title: '',
  description: '',
  content: '',
  status: 'inactive',
};

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? match[1] : null;
};

const getUser = () => {
  const raw = getCookie('dashboard_login_user_creds');
  if (!raw) return {};
  try {
    return JSON.parse(decodeURIComponent(raw));
  } catch (error) {
    return {};
  }
};

const statusClass = (status) => {
  if (status === 'active') return 'bg-green-100 text-green-800';
  if (status === 'archived') return 'bg-gray-200 text-gray-700';
  return 'bg-red-100 text-red-800';
};

const McpPromptManagement = ({ clientID, serviceID, directPromptId, directMode }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const user = useMemo(() => getUser(), []);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [versions, setVersions] = useState([]);

  const commonContext = useMemo(() => ({
    client_id: clientID || user.client_id,
    service_id: serviceID || user.service_id || 'DEFAULT',
    email: user.email,
    menu: 'mcp_prompt_management',
  }), [clientID, serviceID, user.client_id, user.email, user.service_id]);

  const loadPrompts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await server.getMcpPromptManagementList({
        ...commonContext,
        status: statusFilter,
        search,
      });
      if (response.status !== 200) {
        showToast('Error loading MCP prompts', 'error');
        return;
      }
      const body = await response.json();
      setPrompts(body.data?.prompts || []);
    } catch (error) {
      showToast('Error loading MCP prompts', 'error');
    } finally {
      setLoading(false);
    }
  }, [commonContext, search, showToast, statusFilter]);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  const startCreate = () => {
    if (directMode !== 'create') {
      navigate('/mcp-prompt-management/create');
    }
    setSelectedPromptId(null);
    setVersions([]);
    setForm(EMPTY_FORM);
  };

  const startEdit = useCallback(async (prompt) => {
    try {
      if (prompt.prompt_id && directPromptId !== prompt.prompt_id) {
        navigate(`/mcp-prompt-management/${prompt.prompt_id}/edit`);
      }
      const response = await server.getMcpPromptManagementDetails({
        ...commonContext,
        prompt_id: prompt.prompt_id,
      });
      if (response.status !== 200) {
        showToast('Error loading prompt details', 'error');
        return;
      }
      const body = await response.json();
      const details = body.data;
      setSelectedPromptId(details.prompt_id);
      setForm({
        prompt_key: details.prompt_key || '',
        title: details.title || '',
        description: details.description || '',
        content: details.content || '',
        status: details.status || 'inactive',
      });
      setVersions(details.versions || []);
    } catch (error) {
      showToast('Error loading prompt details', 'error');
    }
  }, [commonContext, directPromptId, navigate, showToast]);

  useEffect(() => {
    if (directMode === 'create') {
      setSelectedPromptId(null);
      setVersions([]);
      setForm(EMPTY_FORM);
      return;
    }
    if (directPromptId) {
      startEdit({ prompt_id: directPromptId });
    }
  }, [directPromptId, directMode, startEdit]);

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const savePrompt = async () => {
    if (!form.prompt_key || !form.title || !form.content) {
      showToast('Prompt key, title, and content are required', 'error');
      return;
    }
    if (!PROMPT_KEY_RE.test(form.prompt_key)) {
      showToast('Prompt key can only contain letters, numbers, and hyphen. Spaces are not allowed', 'error');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...commonContext,
        ...form,
        prompt_id: selectedPromptId,
      };
      const response = selectedPromptId
        ? await server.updateMcpPromptManagement(payload)
        : await server.createMcpPromptManagement(payload);
      const body = await response.json();
      if (response.status !== 200) {
        showToast(body.error || 'Error saving prompt', 'error');
        return;
      }
      showToast('MCP prompt saved', 'success');
      const saved = body.data;
      const wasCreating = !selectedPromptId;
      setSelectedPromptId(saved.prompt_id);
      setVersions(saved.versions || []);
      if (wasCreating && saved.prompt_id) {
        navigate(`/mcp-prompt-management/${saved.prompt_id}/edit`, { replace: true });
      }
      await loadPrompts();
    } catch (error) {
      showToast('Error saving prompt', 'error');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (prompt, nextStatus) => {
    try {
      const response = await server.updateMcpPromptManagementStatus({
        ...commonContext,
        prompt_id: prompt.prompt_id,
        status: nextStatus,
      });
      const body = await response.json();
      if (response.status !== 200) {
        showToast(body.error || 'Error updating status', 'error');
        return;
      }
      showToast('Prompt status updated', 'success');
      await loadPrompts();
      if (selectedPromptId === prompt.prompt_id) {
        await startEdit(prompt);
      }
    } catch (error) {
      showToast('Error updating status', 'error');
    }
  };

  const filteredPrompts = prompts.filter((prompt) => {
    const term = search.toLowerCase();
    return (
      !term ||
      prompt.prompt_key?.toLowerCase().includes(term) ||
      prompt.title?.toLowerCase().includes(term) ||
      prompt.description?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="p-6 bg-gray-50 min-h-full">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">MCP Prompt Management</h1>
          <p className="text-sm text-gray-600 mt-1">Manage active MCP prompt templates exposed to clients.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadPrompts}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            onClick={startCreate}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-black text-white text-sm hover:bg-gray-800"
          >
            <Plus size={16} />
            New Prompt
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(360px,45%)_1fr] gap-5">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex gap-3">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') loadPrompts();
              }}
              placeholder="Search prompts"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="w-36 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {loading ? (
            <div className="p-8 text-center text-sm text-gray-500">Loading prompts...</div>
          ) : filteredPrompts.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">No MCP prompts found.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredPrompts.map((prompt) => (
                <div key={prompt.prompt_id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 truncate">{prompt.title}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${statusClass(prompt.status)}`}>
                          {prompt.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{prompt.prompt_key}</p>
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">{prompt.description}</p>
                      <p className="text-xs text-gray-400 mt-2">Version {prompt.current_version || '-'}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => startEdit(prompt)}
                        className="p-2 text-gray-700 hover:bg-gray-100 rounded-md"
                        title="Edit"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        onClick={() => changeStatus(prompt, 'active')}
                        className="p-2 text-green-700 hover:bg-green-50 rounded-md"
                        title="Activate"
                      >
                        <CheckCircle size={16} />
                      </button>
                      <button
                        onClick={() => changeStatus(prompt, 'inactive')}
                        className="p-2 text-red-700 hover:bg-red-50 rounded-md"
                        title="Deactivate"
                      >
                        <XCircle size={16} />
                      </button>
                      <button
                        onClick={() => changeStatus(prompt, 'archived')}
                        className="p-2 text-gray-700 hover:bg-gray-100 rounded-md"
                        title="Archive"
                      >
                        <Archive size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedPromptId ? 'Edit Prompt' : 'Create Prompt'}
            </h2>
            <button
              onClick={savePrompt}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-black text-white text-sm disabled:opacity-60 hover:bg-gray-800"
            >
              <Save size={16} />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Prompt Key</span>
              <input
                value={form.prompt_key}
                onChange={(event) => updateField('prompt_key', event.target.value)}
                disabled={Boolean(selectedPromptId)}
                placeholder="marketing-insights"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-100"
              />
              <p className="mt-1 text-xs text-gray-500">Use only letters, numbers, and hyphen. Spaces are not allowed.</p>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Status</span>
              <select
                value={form.status}
                onChange={(event) => updateField('status', event.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
              >
                <option value="inactive">Inactive</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </label>
          </div>

          <label className="block mb-4">
            <span className="text-sm font-medium text-gray-700">Title</span>
            <input
              value={form.title}
              onChange={(event) => updateField('title', event.target.value)}
              placeholder="Anthropod Marketing Insights"
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </label>

          <label className="block mb-4">
            <span className="text-sm font-medium text-gray-700">Description</span>
            <textarea
              value={form.description}
              onChange={(event) => updateField('description', event.target.value)}
              rows={3}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Prompt Content</span>
            <textarea
              value={form.content}
              onChange={(event) => updateField('content', event.target.value)}
              rows={20}
              spellCheck={false}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono leading-6"
            />
          </label>

          {versions.length > 0 && (
            <div className="mt-5 border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Versions</h3>
              <div className="space-y-2 max-h-44 overflow-y-auto">
                {versions.map((version) => (
                  <div key={version.version_id} className="flex items-center justify-between text-sm border border-gray-100 rounded-md px-3 py-2">
                    <span>Version {version.version}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${statusClass(version.status)}`}>
                      {version.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default McpPromptManagement;
